// Ustawienia (owner, 2026-09-30): "Wyloguj się" moved off the front yard into a modal opened by the
// always-visible gear (audio/AudioControls.js, next to the mute buttons), in whichever scene is on
// screen. "Od nowa" (start the game over) was removed from it (owner, 2026-10-27). "Statystyki" opens the
// player's stats page (StatsModal.js), above Wyloguj się and in its colour (owner, 2026-10-27). "Wygląd" changes the
// player's skin / eyes / hair (LookPanel.js, character look 2026-10-29).
import Phaser from 'phaser';
import { SCENE, GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { gameState } from '../state/gameState.js';
import { api } from '../api/client.js';
import { realtime } from '../api/realtime.js';
import { lockInput, unlockInput, isInputLocked } from './inputLock.js';
import { trackPanel, closeTrackedPanel } from './panelSwitch.js';
import { gamePanel, gameButton, woodTitle } from './gameUi.js';
import { audioManager } from '../audio/AudioManager.js';
import { leaveSafely } from './leaveSafely.mjs';
import { quickLogins } from '../auth/quickLogin.mjs';
import { enlargeForTouch } from './touchEnlarge.js';
import { hideInactiveOverlay } from './inactiveOverlay.js';
import { openStats } from './StatsModal.js';
import { openLookPanel } from './LookPanel.js';
import { showToast } from './Toast.js';
import { messageFor } from '../api/errors.js';

export async function logout(scene) {
  lockInput(scene);                          // no new farm work while we settle and leave
  await leaveSafely(scene, async () => {});
  // the quick-login circle shows her as she leaves (character + what she wears now)
  quickLogins.setLook(window.__myId, { character: window.__myCharacter, equipped: gameState.raw?.inventory?.equipped ?? null,
                                      look: window.__myLook ?? null });
  try { await api.logout(); } catch (_) { /* clear client state regardless */ }
  gameState.invalidate();  // also fences off in-flight responses from this session
  // The WS was authenticated at handshake — close it so the next login gets its own.
  realtime.disconnect();
  hideInactiveOverlay();
  window.__myId = null; window.__myCharacter = null; window.__myEquipped = null;
  window.__myLook = null;
  scene.scene.start(SCENE.LOGIN);
}

// The server says she isn't logged in any more (the login ran out, another tab logged out or into
// someone else): nothing she does here can be saved, so go to the login — her circle is one tap.
export function sessionLost(scene) {
  gameState.invalidate();
  realtime.disconnect();
  hideInactiveOverlay();
  window.__myId = null; window.__myCharacter = null; window.__myEquipped = null;
  window.__myLook = null;
  scene.scene.start(SCENE.LOGIN, { note: 'Zaloguj się jeszcze raz' });
}

// Wygląd from Ustawienia: saved on the server, then on the player's own pictures, to everyone in the room (the
// outfit message, as for a change of clothes) and on their login circle.
function openMyLook(scene) {
  openLookPanel(scene, {
    character: window.__myCharacter ?? 'natalka', look: window.__myLook ?? null,
    equipped: gameState.raw?.inventory?.equipped ?? null,
    onDone: async (look) => {
      try { applyMyLook(scene, (await api.putLook(look))?.look ?? null); }
      catch (e) { showToast(scene, messageFor(e)); throw e; }
    },
  });
}

export function applyMyLook(scene, look) {
  window.__myLook = look;
  for (const s of scene.game.scene.getScenes(true)) {
    s.natalka?.setLook?.(look); s.bike?.rider?.setLook?.(look);
    s.carCtl?.refreshOccupants?.();
  }
  const equipped = gameState.raw?.inventory?.equipped ?? null;
  realtime.outfit({ equipped, cloth_tweaks: gameState.raw?.cloth_tweaks ?? {}, look });
  quickLogins.setLook(window.__myId, { character: window.__myCharacter, equipped, look });
}

// The modal itself, at the camera's view centre (like Modal.js, so it works in scrolling scenes).
export function openSettings(scene) {
  if (!scene) return;
  // The gear with a window open (Rynek, Znajomi, ...) switches to Ustawienia; with Ustawienia open it closes them.
  if (closeTrackedPanel(scene) === 'settings' || isInputLocked(scene)) return;
  const cam = scene.cameras.main;
  const ox = cam.scrollX, oy = cam.scrollY, x = ox + GAME_WIDTH / 2, y = oy + GAME_HEIGHT / 2;
  const W = 420, H = 410;
  const layer = scene.add.container(0, 0).setDepth(2000);
  lockInput(scene);
  let closed = false;
  let untrack = () => {};
  const close = () => { if (closed) return; closed = true; untrack(); unlockInput(scene); layer.destroy(); };
  untrack = trackPanel(scene, 'settings', close);
  scene.events.once('shutdown', close);          // the scene was replaced while it was open
  const overlay = scene.add.rectangle(ox, oy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6).setOrigin(0, 0).setInteractive();
  overlay.on('pointerup', () => { audioManager.playSfx('click'); close(); });
  const panel = gamePanel(scene, x, y, W, H);
  panel.setInteractive(new Phaser.Geom.Rectangle(-W / 2, -H / 2, W, H), Phaser.Geom.Rectangle.Contains);   // taps on the panel don't close it
  const title = woodTitle(scene, x, y - H / 2, 'Ustawienia {gear}');
  const BW = 280, BH = 56;
  const look = gameButton(scene, x, y - 98, BW, BH, '{sparkle} Wygląd', () => { close(); openMyLook(scene); }, { variant: 'secondary', fontSize: 20 });
  const stats = gameButton(scene, x, y - 24, BW, BH, '{medal} Statystyki', () => { close(); openStats(scene); }, { variant: 'secondary', fontSize: 20 });
  const out = gameButton(scene, x, y + 50, BW, BH, '{door} Wyloguj się', () => { close(); logout(scene); }, { variant: 'secondary', fontSize: 20 });
  const shut = gameButton(scene, x, y + 140, 200, 48, 'Zamknij', close, { variant: 'wood', fontSize: 18 });
  layer.add([overlay, panel, title, look, stats, out, shut]);
  enlargeForTouch(scene, layer, { x, y, w: W, h: H }, { overlay });
}

// The scene the player is looking at: the last active one that draws something (not the preloader).
export function topScene(game) {
  const active = game.scene.getScenes(true).filter((s) => s.scene.key !== 'PreloadScene' && s.scene.key !== 'BootScene');
  return active[active.length - 1] ?? null;
}
